/**
 * EVENTSFLO-COMPANY-DOCUMENTS-CLIENT-PORTAL-DOWNLOADS-V1
 *
 * Admin Settings page for managing tenant-wide Company Documents
 * (insurance certificates, T&Cs, privacy, H&S, etc.) and publishing
 * them to the Client Portal.
 *
 * V1 visibility:
 *   - Internal only
 *   - Visible to all clients
 *   - Selected clients (schema-ready; UI exposes a comma-separated list)
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  FileText, Upload, Download, Archive, Trash2, Loader2,
  Eye, EyeOff, Users, Lock, Inbox,
} from "lucide-react";
import {
  listCompanyDocuments, uploadCompanyDocument, updateCompanyDocument,
  deleteCompanyDocument, downloadCompanyDocument,
} from "../lib/api";

const CATEGORIES = [
  { value: "insurance", label: "Insurance" },
  { value: "privacy", label: "Privacy" },
  { value: "terms", label: "Terms of Business" },
  { value: "health_safety", label: "Health & Safety" },
  { value: "operations", label: "Operations" },
  { value: "compliance", label: "Compliance" },
  { value: "other", label: "Other" },
];

const VISIBILITY_OPTIONS = [
  { value: "internal_only", label: "Internal only", Icon: Lock,
    description: "Visible to admins only — not exposed in the Client Portal." },
  { value: "all_clients", label: "Visible to all clients", Icon: Users,
    description: "Every client of this tenant can download from their Client Portal." },
  { value: "selected_clients", label: "Selected clients", Icon: Eye,
    description: "Only listed client IDs can view (V1 — type comma-separated IDs)." },
];

function fileSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function visibilityBadge(value) {
  const v = VISIBILITY_OPTIONS.find((o) => o.value === value);
  if (!v) return null;
  const Icon = v.Icon;
  const tone =
    value === "internal_only"
      ? "border-zinc-500/30 bg-zinc-500/10 text-zinc-300"
      : value === "all_clients"
        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
        : "border-amber-500/30 bg-amber-500/10 text-amber-300";
  return (
    <Badge
      variant="outline"
      className={`gap-1 text-[10px] ${tone}`}
      data-testid={`company-doc-visibility-${value}`}
    >
      <Icon className="h-3 w-3" />
      {v.label}
    </Badge>
  );
}

export default function CompanyDocumentsSettingsPage() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "other",
    version: "",
    visibility: "internal_only",
    selected_client_ids: "",
    expires_at: "",
    file: null,
  });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCompanyDocuments();
      setDocs(res.data?.items || []);
    } catch {
      toast.error("Failed to load company documents");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.file) {
      toast.error("Please select a file");
      return;
    }
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", form.file);
      fd.append("title", form.title);
      if (form.description) fd.append("description", form.description);
      fd.append("category", form.category);
      if (form.version) fd.append("version", form.version);
      fd.append("visibility", form.visibility);
      if (form.selected_client_ids) {
        fd.append("selected_client_ids", form.selected_client_ids);
      }
      if (form.expires_at) fd.append("expires_at", form.expires_at);
      await uploadCompanyDocument(fd);
      toast.success("Document uploaded");
      setForm({
        title: "", description: "", category: "other", version: "",
        visibility: "internal_only", selected_client_ids: "",
        expires_at: "", file: null,
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
      reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Upload failed",
      );
    } finally {
      setUploading(false);
    }
  };

  const handleVisibility = async (doc, visibility) => {
    try {
      await updateCompanyDocument(doc.id, { visibility });
      toast.success("Visibility updated");
      reload();
    } catch {
      toast.error("Failed to update visibility");
    }
  };

  const handleArchiveToggle = async (doc) => {
    const next = doc.status === "active" ? "archived" : "active";
    try {
      await updateCompanyDocument(doc.id, { status: next });
      toast.success(next === "archived" ? "Document archived" : "Document restored");
      reload();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await downloadCompanyDocument(doc.id);
      const blob = new Blob([res.data], { type: doc.mime_type });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.display_filename || doc.filename || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCompanyDocument(deleteTarget.id);
      toast.success("Document deleted");
      setDeleteTarget(null);
      reload();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl" data-testid="company-documents-settings">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
          <FileText className="h-6 w-6 text-primary" />
          Company Documents
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Upload insurance certificates, terms of business, privacy policies and
          other tenant-wide documents. Documents marked <em>Visible to all clients</em>
          {" "}appear in your Client Portal under the Documents tab.
        </p>
      </div>

      {/* Upload */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Upload a new document
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpload} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="cd-file">File *</Label>
              <Input
                id="cd-file"
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.txt"
                onChange={(e) =>
                  setForm((p) => ({ ...p, file: e.target.files?.[0] || null }))
                }
                data-testid="company-doc-file-input"
                className="bg-input border-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Max 25 MB. PDF / Office / image / plain-text only.
              </p>
            </div>
            <div>
              <Label htmlFor="cd-title">Title *</Label>
              <Input
                id="cd-title"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Public Liability Insurance 2026"
                data-testid="company-doc-title-input"
                className="bg-input border-input"
              />
            </div>
            <div>
              <Label htmlFor="cd-category">Category</Label>
              <Select
                value={form.category}
                onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}
              >
                <SelectTrigger
                  id="cd-category"
                  data-testid="company-doc-category-trigger"
                  className="bg-input border-input"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="cd-version">Version</Label>
              <Input
                id="cd-version"
                value={form.version}
                onChange={(e) => setForm((p) => ({ ...p, version: e.target.value }))}
                placeholder="e.g. v2026-01"
                data-testid="company-doc-version-input"
                className="bg-input border-input"
              />
            </div>
            <div>
              <Label htmlFor="cd-expires">Expires</Label>
              <Input
                id="cd-expires"
                type="date"
                value={form.expires_at}
                onChange={(e) =>
                  setForm((p) => ({ ...p, expires_at: e.target.value }))
                }
                data-testid="company-doc-expires-input"
                className="bg-input border-input"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="cd-description">Description</Label>
              <Textarea
                id="cd-description"
                rows={2}
                value={form.description}
                onChange={(e) =>
                  setForm((p) => ({ ...p, description: e.target.value }))
                }
                placeholder="Short summary shown to clients."
                data-testid="company-doc-description-input"
                className="bg-input border-input"
              />
            </div>
            <div>
              <Label>Visibility</Label>
              <Select
                value={form.visibility}
                onValueChange={(v) => setForm((p) => ({ ...p, visibility: v }))}
              >
                <SelectTrigger
                  data-testid="company-doc-visibility-trigger"
                  className="bg-input border-input"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VISIBILITY_OPTIONS.map((v) => (
                    <SelectItem key={v.value} value={v.value}>
                      {v.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {VISIBILITY_OPTIONS.find((o) => o.value === form.visibility)?.description}
              </p>
            </div>
            {form.visibility === "selected_clients" && (
              <div>
                <Label htmlFor="cd-selected">Selected Client IDs</Label>
                <Input
                  id="cd-selected"
                  value={form.selected_client_ids}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, selected_client_ids: e.target.value }))
                  }
                  placeholder="comma-separated client IDs"
                  data-testid="company-doc-selected-clients-input"
                  className="bg-input border-input"
                />
              </div>
            )}
            <div className="md:col-span-2 flex justify-end">
              <Button
                type="submit"
                disabled={uploading}
                data-testid="company-doc-upload-btn"
                className="gap-2"
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                Upload Document
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">All Company Documents</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading…
            </div>
          ) : docs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-muted-foreground"
                 data-testid="company-docs-empty">
              <Inbox className="h-10 w-10 mb-2 opacity-30" />
              <p className="text-sm">No documents uploaded yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="company-docs-table">
                <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                  <tr>
                    <th className="text-left py-2 px-2">Title</th>
                    <th className="text-left py-2 px-2">Category</th>
                    <th className="text-left py-2 px-2">Visibility</th>
                    <th className="text-left py-2 px-2">Version</th>
                    <th className="text-left py-2 px-2">Size</th>
                    <th className="text-left py-2 px-2">Status</th>
                    <th className="text-right py-2 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((d) => (
                    <tr key={d.id} className="border-b border-border/40"
                        data-testid={`company-doc-row-${d.id}`}>
                      <td className="py-2 px-2">
                        <div className="font-medium text-foreground">{d.title}</div>
                        {d.description && (
                          <div className="text-xs text-muted-foreground line-clamp-2 max-w-md">
                            {d.description}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-2 capitalize">
                        {d.category?.replace("_", " ")}
                      </td>
                      <td className="py-2 px-2">
                        <Select
                          value={d.visibility}
                          onValueChange={(v) => handleVisibility(d, v)}
                        >
                          <SelectTrigger
                            className="h-8 w-44 bg-input border-input text-xs"
                            data-testid={`company-doc-visibility-edit-${d.id}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {VISIBILITY_OPTIONS.map((v) => (
                              <SelectItem key={v.value} value={v.value}>
                                {v.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="mt-1">{visibilityBadge(d.visibility)}</div>
                      </td>
                      <td className="py-2 px-2 text-muted-foreground">
                        {d.version || "—"}
                      </td>
                      <td className="py-2 px-2 text-muted-foreground">
                        {fileSize(d.file_size)}
                      </td>
                      <td className="py-2 px-2">
                        <Badge
                          variant="outline"
                          className={
                            d.status === "active"
                              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                              : "border-zinc-500/30 bg-zinc-500/10 text-zinc-400"
                          }
                          data-testid={`company-doc-status-${d.id}`}
                        >
                          {d.status}
                        </Badge>
                      </td>
                      <td className="py-2 px-2">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownload(d)}
                            data-testid={`company-doc-download-${d.id}`}
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleArchiveToggle(d)}
                            data-testid={`company-doc-archive-${d.id}`}
                            title={d.status === "active" ? "Archive" : "Restore"}
                          >
                            {d.status === "active" ? (
                              <Archive className="h-4 w-4" />
                            ) : (
                              <EyeOff className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteTarget(d)}
                            data-testid={`company-doc-delete-${d.id}`}
                            className="text-destructive"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete confirm */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">
              Delete this document?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              <strong>{deleteTarget?.title}</strong> will be permanently
              removed from storage. Clients with cached links will receive a
              404. Consider archiving instead if you may need this document
              later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="company-doc-delete-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="company-doc-delete-confirm"
              className="bg-destructive text-destructive-foreground"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
